import {showDialog} from '@dvrd/dvr-controls';
import {ResponseData} from '@dvrd/fetch';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {WaitlistDay, WaitlistDisplay} from '../../enums';
import {ActivateHandle} from '../../interfaces';
import WaitlistEntry from '../../models/waitlistEntryModel';
import {patchWaitlistEntries} from '../../util/requests';
import EntryDataController from './entry/entryDataController';
import Waitlist from './waitlist';

export default function WaitlistController() {
    const [entries, setEntries] = useState<Array<WaitlistEntry>>([]);
    const [loading, setLoading] = useState(true);
    // Mobile support
    const [display, setDisplay] = useState<WaitlistDisplay>(WaitlistDisplay.WEEK);
    const [day, setDay] = useState<WaitlistDay>(WaitlistDay.MONDAY);

    const dataRef = useRef<ActivateHandle>(null);
    const groupedEntries = useMemo(() => {
        const groups: Record<WaitlistDay, Array<WaitlistEntry>> = {
            [WaitlistDay.MONDAY]: [],
            [WaitlistDay.TUESDAY]: [],
            [WaitlistDay.WEDNESDAY]: [],
            [WaitlistDay.THURSDAY]: [],
            [WaitlistDay.FRIDAY]: [],
            [WaitlistDay.MISC]: []
        };
        for (const entry of entries)
            for (const day of entry.days)
                groups[day].push(entry);
        for (const entries of Object.values(groups))
            entries.sort((entryA: WaitlistEntry, entryB: WaitlistEntry) => entryA.index - entryB.index);
        return groups;
    }, [entries]);

    function onToggleDisplay() {
        setDisplay(
            (currentDisplay) => currentDisplay === WaitlistDisplay.WEEK ? WaitlistDisplay.DAY : WaitlistDisplay.WEEK);
    }

    function onClickDay(day: WaitlistDay) {
        return function () {
            setDay(day);
            setDisplay(WaitlistDisplay.DAY);
        };
    }

    function onChangeDay(direction: 'left' | 'right') {
        return function () {
            if (direction === 'left') {
                if (day === WaitlistDay.MONDAY) return;
                setDay({
                    [WaitlistDay.TUESDAY]: WaitlistDay.MONDAY,
                    [WaitlistDay.WEDNESDAY]: WaitlistDay.TUESDAY,
                    [WaitlistDay.THURSDAY]: WaitlistDay.WEDNESDAY,
                    [WaitlistDay.FRIDAY]: WaitlistDay.THURSDAY,
                    [WaitlistDay.MISC]: WaitlistDay.FRIDAY
                }[day]);
            } else {
                if (day === WaitlistDay.MISC) return;
                setDay({
                    [WaitlistDay.FRIDAY]: WaitlistDay.MISC,
                    [WaitlistDay.THURSDAY]: WaitlistDay.FRIDAY,
                    [WaitlistDay.WEDNESDAY]: WaitlistDay.THURSDAY,
                    [WaitlistDay.TUESDAY]: WaitlistDay.WEDNESDAY,
                    [WaitlistDay.MONDAY]: WaitlistDay.TUESDAY
                }[day]);
            }
        };
    }

    function onClickEntry(entry: WaitlistEntry) {
        return function () {
            dataRef.current?.activate(entry);
        };
    }

    function onAddEntry() {
        dataRef.current?.activate();
    }

    function onMoveEntry(entry: WaitlistEntry, direction: 'up' | 'down' | 'left' | 'right') {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            if (direction === 'up' || direction === 'down') onMoveIndex(entry, direction);
            else onMoveDay(entry, direction);
        };
    }

    function onMoveDay(entry: WaitlistEntry, direction: 'left' | 'right') {
        if (entry.days.length !== 1) return;
        const day = entry.days[0];
        let newDay: WaitlistDay;
        if (direction === 'left') {
            // Previous day
            if (day === WaitlistDay.MONDAY) return;
            newDay = {
                [WaitlistDay.TUESDAY]: WaitlistDay.MONDAY,
                [WaitlistDay.WEDNESDAY]: WaitlistDay.TUESDAY,
                [WaitlistDay.THURSDAY]: WaitlistDay.WEDNESDAY,
                [WaitlistDay.FRIDAY]: WaitlistDay.THURSDAY,
                [WaitlistDay.MISC]: WaitlistDay.FRIDAY
            }[day];
        } else {
            if (day === WaitlistDay.MISC) return;
            newDay = {
                [WaitlistDay.MONDAY]: WaitlistDay.TUESDAY,
                [WaitlistDay.TUESDAY]: WaitlistDay.WEDNESDAY,
                [WaitlistDay.WEDNESDAY]: WaitlistDay.THURSDAY,
                [WaitlistDay.THURSDAY]: WaitlistDay.FRIDAY,
                [WaitlistDay.FRIDAY]: WaitlistDay.MISC
            }[day];
        }
        setLoading(true);
        // Set entry to last
        entry.days = [newDay];
        entry.index = Math.max(...groupedEntries[newDay].map((entry: WaitlistEntry) => entry.index)) + 1;
        entry.update((_: WaitlistEntry, success: boolean, data: ResponseData) => {
            if (success) {
                loadEntries(() => {
                    setDay(newDay);
                });
            } else {
                setLoading(false);
                showDialog(data.message ?? 'Het verplaatsen van de klant is niet gelukt.', 'Verplaatsen mislukt');
            }
        });
    }

    function onMoveIndex(entry: WaitlistEntry, direction: 'up' | 'down') {
        if(entry.days.length !== 1) return;
        const entries = groupedEntries[entry.days[0]];
        if (entries.length <= 1) return;
        const currentIndex = entries.indexOf(entry);
        const currentIndexValue = entry.index;
        let otherEntry: WaitlistEntry;
        if (direction === 'up') {
            // Move up
            if (currentIndex === 0) return;
            otherEntry = entries[currentIndex - 1];
        } else {
            // Move down
            if (currentIndex === entries.length - 1) return;
            otherEntry = entries[currentIndex + 1];
        }
        entry.index = otherEntry.index;
        otherEntry.index = currentIndexValue;
        setLoading(true);
        patchWaitlistEntries({
            data: {
                entries: [
                    {entry_id: entry.id, index: entry.index},
                    {entry_id: otherEntry.id, index: otherEntry.index}
                ]
            }, callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) loadEntries();
                else showDialog(data.message ?? 'Het verplaatsen van de klant is niet gelukt.', 'Verplaatsen mislukt');
            }
        });
    }

    function loadEntries(callback?: VoidFunction) {
        setLoading(true);
        WaitlistEntry.getAll((entries: Array<WaitlistEntry>, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                setEntries(entries);
                callback?.();
            } else showDialog(data.message ?? 'Het laden van de wachtlijst is niet gelukt.', 'Laden mislukt');
        });
    }

    useEffect(() => {
        loadEntries();
    }, []);

    return (
        <>
            <Waitlist onClickEntry={onClickEntry} onAddEntry={onAddEntry} onMoveEntry={onMoveEntry}
                      onToggleDisplay={onToggleDisplay} onChangeDay={onChangeDay} onClickDay={onClickDay}
                      display={display} entries={groupedEntries} loading={loading} day={day}/>
            <EntryDataController ref={dataRef} onSuccess={loadEntries}/>
        </>
    );
}

WaitlistController.displayName = 'WaitlistController';